import type { TaskDueFilter, TaskScope } from '@fos/shared/contracts/work';
import { Plus, SlidersHorizontal } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { useActiveSession } from '../../../lib/auth';
import { cn } from '../../../lib/cn';
import { useScope } from '../../../lib/scope';
import { useAccess } from '../../../lib/useAccess';
import { Button } from '../../../ui/Button';
import { CountPill } from '../../../ui/CountPill';
import { Section } from '../../../ui/Section';
import { Segmented } from '../../../ui/Segmented';
import { SEGMENTED_TOUCH } from '../../mobile';
import { ModulePage } from '../../modules/pages/ModulePage';
import { NewTaskDialog } from '../components/NewTaskDialog';
import { TaskBoard } from '../components/TaskBoard';
import { ClientFilter, FilterBar, FiltersToggle, MobileFilterSheet, statusesFor } from '../components/TaskFilters';
import { TaskList } from '../components/TaskList';
import { DESKTOP_QUERY, useMediaQuery, useTaskOptions } from '../hooks/useTasks';
import {
  countActiveFilters,
  hasExtraFilters,
  parseTaskFilters,
  type TaskFilterState,
  type TaskView,
  toListInput,
  writeTaskFilters,
} from '../lib/filters';

/**
 * /zadania (Figma 04 Zadania: Tablica 3019:2337, Mobile 3037:4999). Desktop: przełącznik Lista / Tablica
 * (zapamiętany), „Wszyscy klienci”, „Filtry”, „Nowe zadanie”; telefon: Lista / Tablica, „Filtry” w dolnym panelu,
 * Dziś / Tydzień / Wszystkie nad listą, tablica przewijana kolumnami z przyciąganiem i „Nowe zadanie” pod kciukiem
 * (lifting 17.10.2026). Filtry są w adresie strony na obu szerokościach.
 * `?nowe=1&klient=ID&projekt=ID` otwiera dialog „Nowe zadanie” z uzupełnionym klientem / projektem.
 */

const VIEW_KEY = 'fos.tasks.view';
const MOBILE_VIEW_KEY = 'fos.tasks.view.mobile';

function storedView(key: string, fallback: TaskView): TaskView {
  try {
    const v = localStorage.getItem(key);
    return v === 'lista' || v === 'tablica' ? v : fallback;
  } catch {
    return fallback;
  }
}

function rememberView(key: string, v: TaskView): void {
  try {
    localStorage.setItem(key, v);
  } catch {
    // tryb prywatny: bez zapamiętywania
  }
}

export function TasksPage() {
  const access = useAccess();
  if (!access.can('tasks')) return <ModulePage module="tasks" />;
  return <TasksScreen />;
}

function TasksScreen() {
  const access = useAccess();
  const session = useActiveSession();
  const { scope } = useScope();
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const [params, setParams] = useSearchParams();
  const filters = parseTaskFilters(params);
  const options = useTaskOptions();
  const canCreate = access.can('tasks', 'edit');
  const [creating, setCreating] = useState(false);
  const me = session.user.id;

  const patch = (p: Partial<TaskFilterState>) => setParams((prev) => writeTaskFilters(prev, p), { replace: true });

  // Dialog z adresu (`?nowe=1`): klient i projekt z adresu to wartości domyślne dialogu, nie filtr.
  const fromUrl = params.get('nowe') === '1' && canCreate;
  const dialogOpen = creating || fromUrl;
  const closeDialog = (open: boolean) => {
    if (open) return;
    setCreating(false);
    if (fromUrl) {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const key of ['nowe', 'klient', 'projekt', 'usluga']) next.delete(key);
          return next;
        },
        { replace: true },
      );
    }
  };
  const dialog = (
    <NewTaskDialog
      open={dialogOpen}
      onOpenChange={closeDialog}
      defaultClientId={filters.clientId ?? null}
      defaultProjectId={filters.projectId ?? null}
      defaultServiceId={fromUrl ? (filters.serviceId ?? null) : null}
    />
  );

  if (!desktop) {
    return (
      <>
        <MobileTasks
          scope={scope}
          me={me}
          canCreate={canCreate}
          onCreate={() => setCreating(true)}
          filters={filters}
          onPatch={patch}
          options={options.data}
        />
        {dialog}
      </>
    );
  }

  const view = filters.view ?? storedView(VIEW_KEY, 'tablica');
  const setView = (v: TaskView) => {
    rememberView(VIEW_KEY, v);
    patch({ view: v });
  };
  const base = toListInput(filters, scope, me);
  const showBar = filters.toolbar || hasExtraFilters(filters);
  const newButton = canCreate ? (
    <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
      Nowe zadanie
    </Button>
  ) : null;

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-[1208px] flex-col px-4 pt-6 pb-10 md:px-12 md:pt-10">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-3">
        <h1 className="text-h3 text-fg">Zadania</h1>
        <ViewToggle value={view} onChange={setView} className="ml-1" />
        <span className="flex-1" />
        <FiltersToggle filters={filters} onPatch={patch} />
        <ClientFilter
          value={filters.clientId}
          options={options.data}
          onChange={(clientId) => patch({ clientId, projectId: undefined, serviceId: undefined })}
        />
        {newButton}
      </header>

      {showBar && (
        <div className="mt-4">
          <FilterBar filters={filters} onPatch={patch} options={options.data} view={view} scope={scope} me={me} />
        </div>
      )}

      <div className="mt-6 flex flex-1 flex-col">
        {view === 'tablica' ? (
          <TaskBoard input={base} emptyAction={newButton} />
        ) : (
          <Section>
            <TaskList
              input={{
                ...base,
                ...(filters.archived && filters.status.length === 0 ? {} : { status: statusesFor(filters) }),
                archived: filters.archived,
                sort: filters.sort,
                limit: 200,
              }}
              emptyTitle={filters.archived ? 'Archiwum jest puste' : 'Brak zadań'}
              emptyDescription={
                hasExtraFilters(filters) || filters.clientId !== undefined
                  ? 'Nic nie pasuje do filtrów.'
                  : 'Dodaj pierwsze zadanie albo przełącz na „Zespół”.'
              }
              emptyAction={filters.archived ? undefined : newButton}
            />
          </Section>
        )}
      </div>
      {dialog}
    </div>
  );
}

/**
 * Przełącznik Lista / Tablica (lifting 17.10.2026): mały segment z lekkim tłem zamiast samego tekstu. Przyciski
 * z `aria-pressed` (stan w adresie strony). `touch` — wyższe pola (44 px) na telefonie.
 */
function ViewToggle({
  value,
  onChange,
  touch = false,
  className,
}: {
  value: TaskView;
  onChange: (view: TaskView) => void;
  touch?: boolean;
  className?: string;
}) {
  return (
    <nav
      aria-label="Widok zadań"
      className={cn('inline-flex items-center gap-0.5 rounded-lg bg-soft p-0.5', className)}
    >
      {(['lista', 'tablica'] as const).map((v) => (
        <button
          key={v}
          type="button"
          aria-pressed={value === v}
          onClick={() => onChange(v)}
          className={cn(
            'rounded-md px-3 text-body-s transition-colors focus-visible:outline-2 focus-visible:outline-accent',
            touch ? 'h-10' : 'h-7',
            value === v ? 'bg-canvas font-medium text-fg shadow-xs' : 'text-fg-secondary hover:text-fg',
          )}
        >
          {v === 'lista' ? 'Lista' : 'Tablica'}
        </button>
      ))}
    </nav>
  );
}

type MobileTab = 'today' | 'week' | 'all';
const MOBILE_TABS: { value: MobileTab; label: string; due?: TaskDueFilter[] }[] = [
  { value: 'today', label: 'Dziś', due: ['overdue', 'today'] },
  { value: 'week', label: 'Tydzień', due: ['overdue', 'week'] },
  { value: 'all', label: 'Wszystkie' },
];

/**
 * Telefon (Figma 3037:4999 + lifting 17.10.2026, 390 px bez przewijania w bok): tytuł H2, Lista / Tablica i „Filtry”
 * (dolny panel z licznikiem aktywnych), nad listą Dziś / Tydzień / Wszystkie, lista z kółkami „Gotowe” (pole dotyku
 * 44 px), tablica przewijana kolumnami z przyciąganiem. „Nowe zadanie” — pływający przycisk nad dolnym menu, po
 * lewej (po prawej jest Copilot).
 */
function MobileTasks({
  scope,
  me,
  canCreate,
  onCreate,
  filters,
  onPatch,
  options,
}: {
  scope: TaskScope;
  me: string;
  canCreate: boolean;
  onCreate: () => void;
  filters: TaskFilterState;
  onPatch: (patch: Partial<TaskFilterState>) => void;
  options: Parameters<typeof MobileFilterSheet>[0]['options'];
}) {
  const [tab, setTab] = useState<MobileTab>('today');
  const [sheet, setSheet] = useState(false);
  const current = MOBILE_TABS.find((t) => t.value === tab) ?? MOBILE_TABS[0];
  const view = filters.view ?? storedView(MOBILE_VIEW_KEY, 'lista');
  const setView = (v: TaskView) => {
    rememberView(MOBILE_VIEW_KEY, v);
    onPatch({ view: v });
  };
  const count = countActiveFilters(filters, { withClient: true });
  const base = toListInput(filters, scope, me);
  const filtered = count > 0;

  return (
    <div className="mx-auto w-full max-w-[480px] px-4 pt-6 pb-32">
      <header className="flex items-center gap-3">
        <h1 className="min-w-0 flex-1 text-h2 text-fg">Zadania</h1>
      </header>
      <div className="mt-5 flex items-center gap-2">
        <ViewToggle value={view} onChange={setView} touch />
        <span className="flex-1" />
        <Button
          variant="ghost"
          size="l"
          icon={SlidersHorizontal}
          onClick={() => setSheet(true)}
          aria-haspopup="dialog"
          className={cn('h-11', filtered && 'bg-soft-hover text-fg')}
        >
          Filtry
          {filtered && <CountPill count={count} tone="accent" label={`, aktywne: ${count}`} />}
        </Button>
      </div>

      {view === 'lista' ? (
        <>
          <Segmented
            className={cn('mt-3', SEGMENTED_TOUCH)}
            fullWidth
            value={tab}
            onValueChange={setTab}
            options={MOBILE_TABS.map((t) => ({ value: t.value, label: t.label }))}
            aria-label="Zakres zadań"
          />
          <Section className="mt-4">
            <TaskList
              variant="mobile"
              input={{
                ...base,
                ...(current?.due ? { due: current.due } : {}),
                ...(filters.archived && filters.status.length === 0 ? {} : { status: statusesFor(filters) }),
                archived: filters.archived,
                sort: filters.sort,
                limit: 200,
              }}
              emptyTitle={
                filtered
                  ? 'Nic nie pasuje do filtrów'
                  : tab === 'today'
                    ? 'Nic na dziś'
                    : tab === 'week'
                      ? 'Nic w tym tygodniu'
                      : 'Brak otwartych zadań'
              }
              emptyDescription={
                filtered ? 'Zmień albo wyczyść filtry.' : 'Dobra robota. Nowe zadanie dodasz przyciskiem na dole.'
              }
            />
          </Section>
        </>
      ) : (
        <div className="mt-4 flex flex-col">
          <TaskBoard input={base} layout="snap" />
        </div>
      )}

      {canCreate && (
        <Button
          variant="primary"
          size="l"
          icon={Plus}
          onClick={onCreate}
          className="fixed bottom-[calc(76px+env(safe-area-inset-bottom)+12px)] left-4 z-30 h-12 rounded-full px-5 shadow-modal"
        >
          Nowe zadanie
        </Button>
      )}

      <MobileFilterSheet
        open={sheet}
        onOpenChange={setSheet}
        filters={filters}
        onPatch={onPatch}
        options={options}
        view={view}
        scope={scope}
        me={me}
      />
    </div>
  );
}
